/**
 * 退票申請歷史 — localStorage 持久化。
 *
 * 同 [[ticket-history]] 動機：BE 無 `GET /me/refunds` endpoint（buyer 為 PII
 * encrypted）；PWA 自 buyer-initiated `POST /refunds` 成功後 push 一筆。
 *
 * 不存敏感資料；僅 refund_id / ticket_id / addedAt。Storage key 含 user_id。
 */

const STORAGE_KEY_PREFIX = 'boat:refund-history:v1';
const MAX_ENTRIES = 50;

export interface RefundHistoryEntry {
  refundId: string;
  ticketId?: string;
  addedAt: string;
}

function storageKey(userId: string | undefined): string {
  return `${STORAGE_KEY_PREFIX}:${userId ?? 'anon'}`;
}

function safeRead(userId: string | undefined): RefundHistoryEntry[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RefundHistoryEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as RefundHistoryEntry).refundId === 'string' &&
        typeof (e as RefundHistoryEntry).addedAt === 'string',
    );
  } catch {
    return [];
  }
}

function safeWrite(userId: string | undefined, entries: RefundHistoryEntry[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(entries));
  } catch {
    // quota exceeded / disabled — silent fail
  }
}

export function listRefundHistory(userId: string | undefined): RefundHistoryEntry[] {
  return safeRead(userId).sort((a, b) => b.addedAt.localeCompare(a.addedAt));
}

export function rememberRefund(
  userId: string | undefined,
  entry: Omit<RefundHistoryEntry, 'addedAt'> & { addedAt?: string },
): void {
  const now = entry.addedAt ?? new Date().toISOString();
  const existing = safeRead(userId);
  const without = existing.filter((e) => e.refundId !== entry.refundId);
  const next: RefundHistoryEntry = {
    refundId: entry.refundId,
    addedAt: now,
    ...(entry.ticketId !== undefined && { ticketId: entry.ticketId }),
  };
  const merged = [next, ...without].slice(0, MAX_ENTRIES);
  safeWrite(userId, merged);
}

export function clearRefundHistory(userId: string | undefined): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}
